import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { AlertTriangle } from 'lucide-react';

/**
 * Card displaying user's strike count with progress bar.
 * Clickable to open strike history modal.
 *
 * Visual states:
 * - 0 strikes: Green progress bar, default styling
 * - 1 strike: Amber progress bar, default styling
 * - 2 strikes: Amber background/border, warning icon
 * - 3+ strikes: Red background/border, danger styling
 *
 * @param {number} strikeCount - Current number of strikes
 * @param {number} maxStrikes - Maximum strikes before lock (default 3)
 * @param {boolean} isLocked - Whether account is locked
 * @param {Function} onClick - Handler for card click (opens history modal)
 */
export default function StrikeSummaryCard({ strikeCount, maxStrikes = 3, isLocked = false, onClick }) {
  const isWarning = strikeCount >= 2;
  const isDanger = strikeCount >= maxStrikes;

  return (
    <Card
      className={`cursor-pointer transition-all hover:shadow-md ${
        isDanger ? 'border-red-500 bg-red-50' :
        isWarning ? 'border-amber-500 bg-amber-50' :
        'hover:bg-gray-50'
      }`}
      onClick={onClick}
    >
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-500 flex items-center gap-1">
              <AlertTriangle className={`h-4 w-4 ${
                isDanger ? 'text-red-500' :
                isWarning ? 'text-amber-500' :
                'text-gray-400'
              }`} />
              <span>Your Strikes</span>
            </div>
            <div className={`text-2xl font-bold ${
              isDanger ? 'text-red-600' :
              isWarning ? 'text-amber-600' :
              'text-gray-700'
            }`}>
              {strikeCount}/{maxStrikes}
            </div>
          </div>
          {isWarning && (
            <AlertTriangle className={`h-8 w-8 ${isDanger ? 'text-red-500' : 'text-amber-500'}`} />
          )}
        </div>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
          <div
            className={`h-2 rounded-full transition-all ${
              isDanger ? 'bg-red-500' :
              isWarning ? 'bg-amber-500' :
              strikeCount > 0 ? 'bg-amber-400' : 'bg-green-500'
            }`}
            style={{ width: `${Math.min((strikeCount / maxStrikes) * 100, 100)}%` }}
          />
        </div>

        {isLocked && (
          <div className="mt-2 text-xs text-red-600 font-medium">
            Account Locked
          </div>
        )}
      </CardContent>
    </Card>
  );
}
